"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import {
  ChevronDown,
  Info,
  LayoutGrid,
  Megaphone,
  Package,
  Settings,
  ShoppingBag,
  UserRoundPlus,
  Workflow,
  Wrench,
  ChevronUp,
} from "lucide-react";
import { GoDotFill } from "react-icons/go";
import { useSidebar } from "./SidebarContext";
import { fetchSubscriptionPlan } from '../lib/Slices/subscriptionSlice';
import ShopifyConnect from '../app/InstallationWizard/Components/Modals/ShopifyConnect';
import { useStore } from '../context/StoreContext';

// Sidebar navigation component for Pixcon dashboard
const Aside = (props) => {
  // Get current path and router for navigation
  const pathname = usePathname();
  const router = useRouter();
  const { isSidebarOpen, closeSidebar } = useSidebar();
  const { selectedStore, stores, setStores, selectStore } = useStore();

  // State for dropdown menus
  const [dropdowns, setDropdowns] = useState({
    marketing: false,
    products: false,
    tools: false,
    settings: false,
    shop: false,
  });
  const [showShopifyModal, setShowShopifyModal] = useState(false);
  
  // Function to refresh stores
  const refreshStores = async () => {
    try {
      const response = await fetch('/api/user-stores');
      const data = await response.json();
      if (data.success && data.stores) {
        console.log('🔄 [Aside] Refreshing stores:', data.stores.length);
        setStores(data.stores);
      } else {
        console.warn('⚠️ [Aside] No stores in response:', data);
        setStores([]);
      }
    } catch (error) {
      console.error('❌ [Aside] Error fetching stores:', error);
      setStores([]);
    }
  };

  // Handle store selection with page reload
  const handleStoreSelect = async (shop) => {
    const success = await selectStore(shop);
    if (success) {
      // Reloads page to ensure all dashboard data refreshes for the new store context
      window.location.reload();
    }
  };

  // Touch/swipe functionality
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Close sidebar on left swipe (swipe left to close)
    if (isLeftSwipe && isSidebarOpen) {
      closeSidebar();
    }
  };

  // Check if a path is active
  const isActive = (path) => pathname === path;

  // Toggle dropdowns, only one open at a time
  const toggleDropdown = (dropdown) => {
    setDropdowns((prev) => {
      // Close all other dropdowns first
      const newState = {
        marketing: false,
        products: false,
        tools: false,
        settings: false,
        shop: false,
      };
      // Toggle the clicked dropdown
      newState[dropdown] = !prev[dropdown];
      return newState;
    });
  };

  // Navigate to a given path
  const navigateTo = (path) => {
    router.push(path);
    // Close sidebar on mobile after navigation
    closeSidebar();
  };

  // Handle click outside sidebar
  const handleClickOutside = (event) => {
    const sidebar = document.getElementById('logo-sidebar');
    const overlay = document.getElementById('sidebar-overlay');

    if (sidebar && !sidebar.contains(event.target) &&
      overlay && !overlay.contains(event.target)) {
      closeSidebar();
    }
  };

  // Load stores on mount and when stores change
  useEffect(() => {
    console.log('🔄 [Aside] Component mounted, loading stores...');
    refreshStores();
  }, []);

  // Sync stores from StoreContext if they change
  useEffect(() => {
    if (stores && stores.length > 0) {
      console.log('✅ [Aside] Stores loaded from context:', stores.length);
    } else {
      console.log('⚠️ [Aside] No stores in context, refreshing...');
      refreshStores();
    }
  }, [stores]);

  // Add event listener for clicks outside
  useEffect(() => {
    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when sidebar is open
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      // Restore body scroll
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  const dispatch = useDispatch();
  const planType = useSelector(state => state.subscription.planType);
  const loading = useSelector(state => state.subscription.loading);

  // Fetch user stores on mount (backup to StoreContext)
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch('/api/user-stores');
        const data = await response.json();
        if (data.success && data.stores) {
          console.log('✅ [Aside] Stores fetched in useEffect:', data.stores.length);
          setStores(data.stores);
        } else {
          console.warn('⚠️ [Aside] No stores in useEffect response:', data);
          setStores([]);
        }
      } catch (error) {
        console.error('❌ [Aside] Error fetching stores in useEffect:', error);
        setStores([]);
      }
    };
    fetchStores();
  }, [setStores]);

  useEffect(() => {
    if (planType === null && !loading) {
      dispatch(fetchSubscriptionPlan());
    }
  }, [dispatch]);

  return (
    <>
      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          id="sidebar-overlay"
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={closeSidebar}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        />
      )}

      <aside
        // Hide sidebar if display_layout prop is true
        style={{ display: props.display_layout ? "none" : "block" }}
        id="logo-sidebar"
        className={`fixed top-0 left-0 z-40 w-64 h-screen pt-[5rem] transition-transform duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
            md:translate-x-0 bg-[#284f9e]`}
        aria-label="Sidebar"
        aria-hidden={!isSidebarOpen ? "true" : "false"}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="h-full flex flex-col bg-[#284f9e]">
          {/* Main Navigation */}
          <div className="flex-1 px-3 flex flex-col justify-between">
            <div className="flex-1">
              <ul className="space-y-2 font-normal">
                {/* Shop Dropdown */}
                <li>
                  <button
                    onClick={() => toggleDropdown("shop")}
                    className="flex items-center px-4 py-3 text-white rounded-lg w-full text-left border border-[#58BAE9] bg-[#15458E] hover:bg-[#1a5ba8] transition-colors"
                  >
                    <Image
                      className="rounded-full border-[3px] border-[#58BAE9]"
                      src={"/images/icons/p.png"}
                      alt="pixcon"
                      width={40}
                      height={40}
                    />
                    <span className="flex-1 ms-3 text-left whitespace-nowrap font-medium">
                      Pixcon
                    </span>
                    {dropdowns.shop ? <ChevronUp size={20} color="#fff" /> : <ChevronDown size={20} color="#fff" />}
                  </button>

                  {/* Shop Submenu */}
                  <ul
                    className={`mt-2 space-y-1 ${dropdowns.shop ? "" : "hidden"}`}
                  >
                    {/* Debug info (remove in production) */}
                    {process.env.NODE_ENV === 'development' && (
                      <li className="pl-8 py-1 text-xs text-gray-400">
                        Stores: {Array.isArray(stores) ? stores.length : 'not array'} | 
                        Selected: {selectedStore || 'none'}
                      </li>
                    )}
                    
                    {/* Dynamic Connected Stores */}
                    {Array.isArray(stores) && stores.length > 0 && stores.map((store) => {
                      console.log('🔄 [Aside] Rendering store:', store);
                      return (
                        <li key={store.id || store.shop}>
                          <button
                            onClick={() => handleStoreSelect(store.shop)}
                            className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors hover:bg-[#15458E] ${
                              selectedStore === store.shop ? 'bg-[#15458E] text-[#58BAE9]' : 'text-white'
                            }`}
                          >
                            <GoDotFill size={16} color={selectedStore === store.shop ? '#58BAE9' : '#fff'} />
                            <span className="text-sm truncate">{store.shop}</span>
                          </button>
                        </li>
                      );
                    })}
                    
                    {/* Show message if no stores */}
                    {(!stores || !Array.isArray(stores) || stores.length === 0) && (
                      <li>
                        <div className="pl-8 py-2 text-gray-400 text-sm">
                          No stores connected
                        </div>
                      </li>
                    )}
                    
                    {/* Connect New Store */}
                    <li>
                      <button
                        onClick={() => setShowShopifyModal(true)}
                        className="flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors text-[#58BAE9] hover:bg-[#15458E] border-t border-[#58BAE9] mt-2 pt-3"
                      >
                        <span className="text-lg">+</span>
                        <span className="text-sm font-medium">Connect New Store</span>
                      </button>
                    </li>
                  </ul>
                </li>

                {/* Dashboard Link */}
                <li>
                  <button
                    onClick={() => navigateTo("/dashboard")}
                    className={`flex items-center px-4 py-3 text-white rounded-lg w-full text-left transition-colors ${isActive("/dashboard")
                        ? "bg-[#58BAE9] shadow-md"
                        : "bg-transparent hover:bg-[#15458E]"
                      }`}
                  >
                    <LayoutGrid size={20} color={isActive("/dashboard") ? '#fff' : '#58BAE9'} />
                    <span className="ms-3 font-medium">Dashboard</span>
                  </button>
                </li>

                {/* Marketing Dropdown */}
                <li>
                  <button
                    onClick={() => toggleDropdown("marketing")}
                    className={`flex items-center w-full px-4 py-3 text-white rounded-lg text-left transition-colors ${pathname.startsWith("/marketing")
                        ? "bg-[#58BAE9] shadow-md"
                        : "bg-transparent hover:bg-[#15458E]"
                      }`}
                  >
                    <Megaphone size={20} color={pathname.startsWith("/marketing") ? '#fff' : '#58BAE9'} />
                    <span className="flex-1 ms-3 text-left whitespace-nowrap font-medium">
                      Marketing
                    </span>
                    {dropdowns.marketing ? <ChevronUp size={20} color={pathname.startsWith("/marketing") ? '#fff' : '#58BAE9'} /> : <ChevronDown size={20} color={pathname.startsWith("/marketing") ? '#fff' : '#58BAE9'} />}
                  </button>

                  {/* Marketing Submenu */}
                  <ul
                    className={`py-2 space-y-1 ${dropdowns.marketing ? "" : "hidden"
                      }`}
                  >
                    {/* Overview */}
                    <li>
                      <button
                        onClick={() => navigateTo("/marketing/overview")}
                        className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors ${isActive("/marketing/overview")
                            ? "text-[#58BAE9]"
                            : "text-white"
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/marketing/overview") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Overview</span>
                      </button>
                    </li>

                    {/* Optimizations */}
                    <li>
                      <button
                        onClick={() => navigateTo("/marketing/opt")}
                        className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors ${isActive("/marketing/opt")
                            ? "text-[#58BAE9]"
                            : "text-white"
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/marketing/opt") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Optimizations</span>
                      </button>
                    </li>

                    {/* Channel Performance */}
                    <li>
                      <button
                        onClick={() => navigateTo("/marketing/sales")}
                        className={`flex items-center gap-2 pl-8 py-1 w-full text-left rounded-md transition-colors ${isActive("/marketing/sales")
                            ? "text-[#58BAE9] bg-[#15458E]"
                            : "text-white  "
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/marketing/sales") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Channel Performance</span>
                      </button>
                    </li>

                    {/* Traffic & Customer */}
                    <li>
                      <button
                        onClick={() => navigateTo("/marketing/traffic")}
                        className={`flex items-center gap-2 pl-8 py-1 w-full text-left rounded-md transition-colors ${isActive("/marketing/traffic")
                            ? "text-[#58BAE9]"
                            : "text-white"
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/marketing/traffic") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Traffic & Customer</span>
                      </button>
                    </li>
                  </ul>
                </li>

                {/* Orders Link */}
                <li>
                  <button
                    onClick={() => navigateTo("/orders")}
                    className={`flex items-center px-4 py-3 text-white rounded-lg w-full text-left transition-colors ${isActive("/orders")
                        ? "bg-[#58BAE9] shadow-md"
                        : "bg-transparent hover:bg-[#15458E]"
                      }`}
                  >
                    <ShoppingBag size={20} color={isActive("/orders") ? '#fff' : '#58BAE9'} />
                    <span className="flex-1 ms-3 whitespace-nowrap font-medium">Orders</span>
                  </button>
                </li>

                {/* Products Dropdown */}
                <li>
                  <button
                    onClick={() => toggleDropdown("products")}
                    className={`w-full flex items-center px-4 py-3 text-white rounded-lg text-left transition-colors ${pathname.startsWith("/products")
                        ? "bg-[#58BAE9] shadow-md"
                        : "bg-transparent hover:bg-[#15458E]"
                      }`}
                  >
                    <Package size={20} color={pathname.startsWith("/products") ? '#fff' : '#58BAE9'} />
                    <span className="flex-1 ms-3 text-left whitespace-nowrap font-medium">
                      Products
                    </span>
                    {dropdowns.products ? <ChevronUp size={20} color={pathname.startsWith("/products") ? '#fff' : '#58BAE9'} /> : <ChevronDown size={20} color={pathname.startsWith("/products") ? '#fff' : '#58BAE9'} />}
                  </button>

                  {/* Products Submenu */}
                  <ul
                    className={`py-2 space-y-1 ${dropdowns.products ? "" : "hidden"}`}
                  >
                    {/* Sales & Return */}
                    <li>
                      <button
                        onClick={() => navigateTo("/products/sales")}
                        className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors ${isActive("/products/sales")
                            ? "text-[#58BAE9]"
                            : "text-white"
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/products/sales") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Sales & Return</span>
                      </button>
                    </li>

                    {/* Action */}
                    <li>
                      <button
                        onClick={() => navigateTo("/products/action")}
                        className={`flex items-center gap-2 pl-8 py-1 w-full text-left rounded-md transition-colors ${isActive("/products/action")
                            ? "text-[#58BAE9]"
                            : "text-white"
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/products/action") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Action</span>
                      </button>
                    </li>

                    {/* Inventory */}
                    <li>
                      <button
                        onClick={() => navigateTo("/products/inventory")}
                        className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors ${isActive("/products/inventory")
                            ? "text-[#58BAE9]"
                            : "text-white"
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/products/inventory") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Inventory</span>
                      </button>
                    </li>
                  </ul>
                </li>

                {/* Tools Dropdown */}
                <li>
                  <button
                    onClick={() => toggleDropdown("tools")}
                    className={`w-full flex items-center px-4 py-3 text-white rounded-lg text-left transition-colors ${pathname.startsWith("/tools")
                        ? "bg-[#58BAE9] shadow-md"
                        : "bg-transparent hover:bg-[#15458E]"
                      }`}
                  >
                    <Wrench size={20} color={pathname.startsWith("/tools") ? '#fff' : '#58BAE9'} />
                    <span className="flex-1 ms-3 text-left whitespace-nowrap font-medium">
                      Tools
                    </span>
                    {dropdowns.tools ? <ChevronUp size={20} color={pathname.startsWith("/tools") ? '#fff' : '#58BAE9'} /> : <ChevronDown size={20} color={pathname.startsWith("/tools") ? '#fff' : '#58BAE9'} />}
                  </button>

                  {/* Tools Submenu */}
                  <ul className={`py-1 space-y-0.5 ${dropdowns.tools ? "" : "hidden"}`}>
                    {/* Break even ROAS Calculator */}
                    <li>
                      <button
                        onClick={() => navigateTo("/tools/calculator")}
                        className={`flex items-center gap-2 pl-8 py-1 w-full text-left rounded-md transition-colors ${isActive("/tools/calculator")
                            ? "text-[#58BAE9]"
                            : "text-white"
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/tools/calculator") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Break even ROAS Calculator</span>
                      </button>
                    </li>

                    {/* Return */}
                    <li>
                      <button
                        onClick={() => navigateTo("/tools/profit")}
                        className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors ${isActive("/tools/profit")
                            ? "text-[#58BAE9]"
                            : "text-white"
                          }`}
                      >
                        <GoDotFill size={16} color={isActive("/tools/profit") ? '#58BAE9' : '#fff'} />
                        <span className="text-sm">Return</span>
                      </button>
                    </li>
                  </ul>
                </li>

                {/* Connections Link */}
                <li>
                  <button
                    onClick={() => navigateTo("/connectivity")}
                    className={`flex items-center px-4 py-3 text-white rounded-lg w-full text-left transition-colors ${isActive("/connectivity")
                        ? "bg-[#58BAE9] shadow-md"
                        : "bg-transparent hover:bg-[#15458E]"
                      }`}
                  >
                    <Workflow size={20} color={isActive("/connectivity") ? '#fff' : '#58BAE9'} />
                    <span className="flex-1 ms-3 whitespace-nowrap font-medium">Connections</span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Settings Section at Bottom of Navigation, above action buttons */}
            <div className="mt-4 pt-4">
              <button
                onClick={() => toggleDropdown("settings")}
                className={`w-full flex items-center px-4 py-3 text-white rounded-lg text-left transition-colors ${pathname.startsWith("/settings")
                    ? "bg-[#58BAE9] shadow-md"
                    : "bg-transparent hover:bg-[#284F9E]"
                  }`}
              >
                <Settings size={20} color={pathname.startsWith("/settings") ? '#fff' : '#58BAE9'} />
                <span className="flex-1 ms-3 text-left whitespace-nowrap font-medium">
                  Settings
                </span>
                {dropdowns.settings ? <ChevronUp size={20} color={pathname.startsWith("/settings") ? '#fff' : '#58BAE9'} /> : <ChevronDown size={20} color={pathname.startsWith("/settings") ? '#fff' : '#58BAE9'} />}
              </button>

              {/* Settings Submenu */}
              <ul className={`py-2 space-y-1 ${dropdowns.settings ? "" : "hidden"}`}>
                {/* Account */}
                <li>
                  <button
                    onClick={() => navigateTo("/settings/account")}
                    className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors ${pathname.startsWith("/settings/account")
                        ? "text-[#58BAE9]"
                        : "text-white"
                      }`}
                  >
                    <GoDotFill size={16} color={pathname.startsWith("/settings/account") ? '#58BAE9' : '#fff'} />
                    <span className="text-sm">Account</span>
                  </button>
                </li>

                {/* Shop */}
                <li>
                  <button
                    onClick={() => navigateTo("/settings/shop")}
                    className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors ${pathname.startsWith("/settings/shop")
                        ? "text-[#58BAE9]"
                        : "text-white"
                      }`}
                  >
                    <GoDotFill size={16} color={pathname.startsWith("/settings/shop") ? '#58BAE9' : '#fff'} />
                    <span className="text-sm">Shop</span>
                  </button>
                </li>

                {/* Calculation */}
                <li>
                  <button
                    onClick={() => navigateTo("/settings/calculation")}
                    className={`flex items-center gap-2 pl-8 py-2 w-full text-left rounded-md transition-colors ${pathname.startsWith("/settings/calculation")
                        ? "text-[#58BAE9]"
                        : "text-white"
                      }`}
                  >
                    <GoDotFill size={16} color={isActive("/settings/calculation") ? '#58BAE9' : '#fff'} />
                    <span className="text-sm">Calculation</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Action Buttons - Fixed at bottom, separated from Settings */}
          <div className="p-4 flex items-center justify-between gap-2">
            {/* Upgrade Plan Button */}
            <button
              className="flex-1 text-sm text-nowrap py-2 px-3 flex items-center justify-center rounded-lg bg-transparent text-[#58BAE9] border border-[#58BAE9] hover:bg-[#58BAE9] hover:text-white transition-colors"
              onClick={() => router.push('/settings/account?pg=subscription')}
            >
              {planType ? `${planType} Plan` : 'Upgrade Plan'}
            </button>

            {/* Add User Button */}
            <button
              className="p-2 rounded-lg bg-transparent text-[#58BAE9] border border-[#58BAE9] hover:bg-[#58BAE9] hover:text-white transition-colors group"
              onClick={() => router.push('/settings/account?pg=team')}
            >
              <UserRoundPlus size={18} className="group-hover:text-white transition-colors" color="currentColor" />
            </button>

            {/* Info Button */}
            <button className="p-2 rounded-lg bg-transparent text-[#58BAE9] border border-[#58BAE9] hover:bg-[#58BAE9] hover:text-white transition-colors group">
              <Info size={18} className="group-hover:text-white transition-colors" color="currentColor" />
            </button>
          </div>
        </div>
      </aside>
      
      {/* ShopifyConnect Modal */}
      {showShopifyModal && (
        <ShopifyConnect 
          toggleModal={(action) => {
            setShowShopifyModal(false);
            if (action === 'success') {
              // Refresh stores list after successful connection
              setTimeout(refreshStores, 1000);
            }
          }}
        />
      )}
    </>
  );
};

// Export the Aside component
export default Aside;